import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton,
    InputAdornment, LinearProgress, Switch, Tab, Tabs, TextField, Tooltip, Typography, useMediaQuery, useTheme} from '@mui/material';
import {Add, Close, DeleteOutline, LockOutlined, Search, ViewColumnOutlined} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {connect} from 'react-redux';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {columnActions} from '../../store/actions/columnAction';
import {DnDColumnsContainer} from './DnDColumnsContainer';
import {ColumnDialog, ColumnSection, columnRowStyles} from './ColumnDesign';
import {SettingsDialogDescription} from '../settings/SettingsDialog';
import AddCustomColumn from '../custom-column/AddCustomColumn';
import {getSettingsColors} from '../settings/SettingsDesign';

const emptyColumns = [];

export function ColumnSettings({onClose, columns = [], open, report, columnGroup, state,
    setCampaignSelectedColumns, updateColumnVisibility, updateUserColumnSettings, deleteCustomMetricColumn}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const narrow = useMediaQuery(theme.breakpoints.down('md'));
    const titleId = React.useId();
    const availableColumns = state?.column?.data?.[columnGroup];
    const selectedColumns = state?.column?.campaignSelectedColumns || emptyColumns;
    const initializedGroup = useRef(null);
    const knownColumnIds = useRef(new Set());
    const [addCustomColumn, setAddCustomColumn] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteColumn, setDeleteColumn] = useState(null);
    const [saving, setSaving] = useState(false);
    const [requestError, setRequestError] = useState('');
    const [panel, setPanel] = useState('visibility');
    const query = searchQuery.trim().toLowerCase();
    const filteredColumns = (availableColumns || []).filter(column =>
        !query || `${column.title || ''} ${column.description || ''}`.toLowerCase().includes(query));

    useEffect(() => {
        if (!open) {
            initializedGroup.current = null;
            knownColumnIds.current = new Set();
            setSearchQuery('');
            return;
        }
        if (availableColumns && initializedGroup.current !== columnGroup) {
            initializedGroup.current = columnGroup;
            setCampaignSelectedColumns(availableColumns.filter(column => !column.hidden));
        } else if (availableColumns) {
            const added = availableColumns.filter(column => !knownColumnIds.current.has(column.id) && !column.hidden);
            if (added.length) setCampaignSelectedColumns([...selectedColumns, ...added]);
        }
        if (availableColumns) knownColumnIds.current = new Set(availableColumns.map(column => column.id));
    }, [open, availableColumns, columnGroup, selectedColumns, setCampaignSelectedColumns]);

    const handleVisibilityChange = column => {
        const hidden = !column.hidden;
        // The reporting grid also reads visibility from its column definitions.
        columns.forEach(item => {if (item.key === column.columnKey) item.hidden = hidden;});
        setCampaignSelectedColumns(hidden
            ? selectedColumns.filter(item => item.columnKey !== column.columnKey)
            : [...selectedColumns, {...column, hidden: false}]);
        updateColumnVisibility({columnID: column.id, hidden, columnGroup});
    };

    const handleClose = (event, reason) => {
        if (reason !== 'backdropClick' && !saving) onClose();
    };

    const handleApply = async () => {
        if (saving || !availableColumns) return;
        const selectedOrder = new Map(selectedColumns.map((column, index) => [column.id, index]));
        const settings = availableColumns.map((column, index) => ({
            name: column.columnKey, columnId: column.id, width: column.width,
            hidden: !selectedOrder.has(column.id), sequenceNumber: selectedOrder.get(column.id) ?? index
        }));
        setSaving(true);
        setRequestError('');
        try {
            await updateUserColumnSettings({report, settings});
            onClose();
        } catch (error) {
            setRequestError(error?.response?.data?.error || 'Unable to save column settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return <>
        <ColumnDialog open={open} onClose={handleClose} fullWidth fullScreen={fullScreen} maxWidth="lg"
            aria-labelledby={titleId}>
            <DialogTitle id={titleId}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Box component="span">Column settings</Box>
                    <IconButton aria-label="Close column settings" onClick={handleClose} disabled={saving} size="small"><Close fontSize="small"/></IconButton>
                </Box>
                <SettingsDialogDescription>Choose the columns to show and arrange their order in your report.</SettingsDialogDescription>
            </DialogTitle>
            <DialogContent>
                {saving && <LinearProgress sx={{position: 'absolute', top: 0, left: 0, right: 0}}/>}
                {requestError && <Alert severity="error" sx={{mb: 2}}>{requestError}</Alert>}
                {narrow && availableColumns && <Tabs value={panel} onChange={(event, value) => setPanel(value)} variant="fullWidth"
                    aria-label="Column settings sections" sx={{mb: 2, p: 0.5, minHeight: 36, borderRadius: '10px',
                        bgcolor: theme => getSettingsColors(theme).inset, '& .MuiTabs-indicator': {display: 'none'},
                        '& .MuiTab-root': {minHeight: 36, minWidth: 0, p: 1, fontSize: 12, textTransform: 'none', borderRadius: '7px',
                            '&.Mui-selected': {bgcolor: theme => alpha(theme.palette.primary.main, 0.1)}}}}>
                    <Tab id={`${titleId}-visibility-tab`} aria-controls={`${titleId}-visibility`} value="visibility" label="Column visibility"/>
                    <Tab id={`${titleId}-order-tab`} aria-controls={`${titleId}-order`} value="order" label="Column order"/>
                </Tabs>}
                {!availableColumns ? <Box sx={{display: 'flex', justifyContent: 'center', py: 6}} role="status" aria-label="Loading columns">
                    <CircularProgress size={28}/>
                </Box> : <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.2fr) minmax(0, 1fr)'}, gap: 2}}>
                    <Box id={`${titleId}-visibility`} role={narrow ? 'tabpanel' : undefined}
                        aria-labelledby={narrow ? `${titleId}-visibility-tab` : undefined} sx={{minWidth: 0, display: narrow && panel !== 'visibility' ? 'none' : 'block'}}>
                    <ColumnSection title="Column visibility" count={availableColumns.length} description="Turn columns on or off. The name column is always visible.">
                        <TextField placeholder="Search columns" size="small" fullWidth value={searchQuery}
                            onChange={event => setSearchQuery(event.target.value)}
                            slotProps={{htmlInput: {'aria-label': 'Search columns'}, input: {startAdornment:
                                <InputAdornment position="start"><Search sx={{fontSize: 19, color: 'text.secondary'}}/></InputAdornment>,
                                endAdornment: searchQuery && <InputAdornment position="end"><IconButton size="small" aria-label="Clear column search"
                                    onClick={() => setSearchQuery('')}><Close fontSize="small"/></IconButton></InputAdornment>}}}/>
                        <Box sx={{display: 'grid', gap: 0.75, mt: 1.5, maxHeight: {xs: 330, md: 430}, overflowY: 'auto', pr: 0.5}}>
                            {filteredColumns.map(column => <Box key={column.id} sx={columnRowStyles}>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Typography sx={{fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere'}}>{column.title}</Typography>
                                    {column.description && <Typography sx={{fontSize: 12, color: 'text.secondary', lineHeight: 1.5, mt: 0.25,
                                        overflowWrap: 'anywhere'}}>{column.description}</Typography>}
                                </Box>
                                {column.columnKey === 'name' ? <Tooltip title="Always visible"><LockOutlined sx={{fontSize: 18, color: 'text.secondary', mx: 1}}/></Tooltip> :
                                    <Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25}}>
                                        {column.type === 'customMetric' && <Tooltip title="Delete custom column"><IconButton size="small" disabled={saving}
                                            aria-label={`Delete ${column.title}`} onClick={() => setDeleteColumn(column)}
                                            sx={{color: 'text.secondary', '&:hover': {color: 'error.main', bgcolor: theme => alpha(theme.palette.error.main, 0.1)}}}>
                                            <DeleteOutline sx={{fontSize: 18}}/>
                                        </IconButton></Tooltip>}
                                        <Switch size="small" checked={!column.hidden} disabled={saving} onChange={() => handleVisibilityChange(column)}
                                            slotProps={{input: {'aria-label': `Show ${column.title}`}}}/>
                                    </Box>}
                            </Box>)}
                            {!filteredColumns.length && <Box sx={{py: 4, textAlign: 'center'}}>
                                <ViewColumnOutlined sx={{fontSize: 30, color: 'text.secondary', mb: 0.75}}/>
                                <Typography sx={{fontSize: 13, fontWeight: 600}}>No columns found</Typography>
                                <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5}}>
                                    {query ? 'Try a different search.' : 'No report columns are available yet.'}
                                </Typography>
                            </Box>}
                        </Box>
                    </ColumnSection>
                    </Box>
                    <Box id={`${titleId}-order`} role={narrow ? 'tabpanel' : undefined}
                        aria-labelledby={narrow ? `${titleId}-order-tab` : undefined} sx={{minWidth: 0, display: narrow && panel !== 'order' ? 'none' : 'block'}}>
                    <ColumnSection title="Column order" count={selectedColumns.length} description="Drag columns or use the arrows to change their order.">
                        <Box sx={{maxHeight: {xs: 330, md: 484}, overflowY: 'auto', pr: 0.5}}>
                            <DndProvider backend={HTML5Backend}>
                                <DnDColumnsContainer cards={selectedColumns} setCards={setCampaignSelectedColumns} disabled={saving}/>
                            </DndProvider>
                            {!selectedColumns.length && <Typography sx={{py: 4, fontSize: 13, color: 'text.secondary', textAlign: 'center'}}>
                                Turn on a column to add it to your report.
                            </Typography>}
                        </Box>
                    </ColumnSection>
                    </Box>
                </Box>}
            </DialogContent>
            <DialogActions disableSpacing sx={{flexWrap: 'wrap', justifyContent: 'space-between'}}>
                <Button startIcon={<Add/>} onClick={() => setAddCustomColumn(true)} disabled={saving || !availableColumns} variant="outlined">New custom column</Button>
                <Box sx={{display: 'flex', gap: 1, ml: 'auto'}}>
                    <Button color="inherit" onClick={handleClose} disabled={saving}>Close</Button>
                    <Button variant="contained" onClick={handleApply} disabled={saving || !availableColumns}
                        startIcon={saving ? <CircularProgress size={16} color="inherit"/> : undefined}>{saving ? 'Applying…' : 'Apply'}</Button>
                </Box>
            </DialogActions>
        </ColumnDialog>
        {addCustomColumn && <AddCustomColumn open onClose={() => setAddCustomColumn(false)}/>}
        {!!deleteColumn && <ColumnDialog open fullWidth maxWidth="xs" onClose={() => setDeleteColumn(null)} aria-labelledby="delete-custom-column-title"
            sx={{'& .MuiDialog-paper': {borderRadius: '16px'}}}>
            <DialogTitle id="delete-custom-column-title">Delete custom column?</DialogTitle>
            <DialogContent><Typography sx={{fontSize: 14, color: 'text.secondary', lineHeight: 1.7, overflowWrap: 'anywhere'}}>
                Delete “{deleteColumn.title}”? This column will be removed from your reports.
            </Typography></DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={() => setDeleteColumn(null)}>Cancel</Button>
                <Button color="error" variant="contained" onClick={() => {deleteCustomMetricColumn({columnId: deleteColumn.id}); setDeleteColumn(null);}}>Delete column</Button>
            </DialogActions>
        </ColumnDialog>}
    </>;
}

export default connect(state => ({state: {column: state.column}}), {
    updateColumnVisibility: columnActions.updateColumnVisibility,
    updateUserColumnSettings: columnActions.updateUserColumnSettings,
    setCampaignSelectedColumns: columnActions.setCampaignSelectedColumns,
    deleteCustomMetricColumn: columnActions.deleteCustomMetricColumn
})(ColumnSettings);
